// Central API client for all ERP entities.
// Set REACT_APP_API_URL in .env.local to your API Gateway invoke URL.
// e.g. REACT_APP_API_URL=https://abc123.execute-api.us-east-1.amazonaws.com/prod

import { guardSave } from '../utils/saveGuard';
import { getIdToken, refreshIdTokenAfter401, hardLogout } from './authToken';
import { assertCanWrite } from './writeGate';
import { recordActivity, setActivitySender } from './activityLog';

// Honors REACT_APP_API_URL (see .env.example), e.g. a local stub gateway for
// layout checks, and falls back to the production stage.
const BASE = (process.env.REACT_APP_API_URL || 'https://nc6qxmqbi1.execute-api.us-east-1.amazonaws.com/prod').replace(/\/$/, '');

// Retry policy for the core `request` helper. The backend lambdas can return
// transient 5xx (cold-start, DDB throttling, brief CORS-preflight timeouts)
// when the dashboard fires its full burst of context loads on first paint,
// the visible symptom is "sometimes loads, sometimes doesn't" depending on
// container warmth. We retry those quietly so the user sees a working app.
//
// We intentionally do NOT retry 4xx, those are real validation errors (e.g.
// "Missing required field") that won't get any better the second time.
const MAX_RETRIES   = 2;        // 3 total attempts (initial + 2 retries)
const BACKOFF_BASE  = 250;      // 250ms, then 750ms, tight enough that the
                                 // user doesn't notice, generous enough to let
                                 // a cold-starting lambda finish booting.

const sleep = ms => new Promise(r => setTimeout(r, ms));

// ─── Core request helper ──────────────────────────────────────────────────────

async function request(path, options = {}) {
    // A view-only page refuses saves before they are sent (api/writeGate.js).
    const method = String(options.method || 'GET').toUpperCase();
    // A POST that only reads (an action-dispatch gateway's list call) opts
    // out with `read: true`: a view-only page may still look.
    if (method !== 'GET' && method !== 'HEAD' && options.read !== true) assertCanWrite({ path, method, body: options.body });
    // Absolute URLs pass through untouched, used by the per-brand staffs API
    // (each brand's gateway hosts its own /staffs stack); everything else is
    // relative to the shared BASE gateway.
    const isAbsolute = /^https?:\/\//i.test(path);
    const url = isAbsolute ? path : `${BASE}${path}`;
    // `authorize` sends the ID token to an absolute URL as well: a per-brand
    // route behind an authorizer on THIS dashboard's user pool (Romanian's
    // /gift-cards-admin). A 401 there only fails that call; the refresh /
    // sign-out handling below stays with the BASE gateway.
    const { authorize, read: _read, ...fetchOptions } = options;
    const sendsToken = !isAbsolute || authorize === true;
    let lastErr;
    let refreshedAfter401 = false;
    let sentToken = false;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        let res;
        try {
            // The BASE gateway's dashboard routes sit behind a Cognito
            // authorizer, send the ID token (refreshed when near expiry).
            // Absolute URLs target the per-brand gateways, which have no
            // authorizer; leave those requests untouched so their CORS
            // contract doesn't change, unless the call opts in above.
            const headers = { 'Content-Type': 'application/json', ...(fetchOptions.headers || {}) };
            if (sendsToken) {
                const token = await getIdToken();
                if (token) { headers.Authorization = token; sentToken = true; }
            }
            res = await fetch(url, { ...fetchOptions, headers });
        } catch (err) {
            // Fetch itself threw, network failure, CORS preflight rejection,
            // or the page was offline. All of these can be transient when a
            // lambda is cold-starting; back off and try again.
            lastErr = err;
            if (attempt >= MAX_RETRIES) throw err;
            await sleep(BACKOFF_BASE * Math.pow(3, attempt));
            continue;
        }

        // Authorizer rejection of a REAL session: the token expired mid-flight
        // (or was minted by a clock ahead of the gateway's). Refresh once and
        // retry immediately; if the session is truly dead, sign back in. When
        // no token was sent at all, the public marketing pages mount the data
        // providers too, a 401 is just the gateway doing its job: surface it
        // like any other error, never redirect an anonymous visitor.
        if (res.status === 401 && !isAbsolute && sentToken) {
            if (!refreshedAfter401 && attempt < MAX_RETRIES) {
                refreshedAfter401 = true;
                lastErr = new Error('HTTP 401');
                const fresh = await refreshIdTokenAfter401();
                if (fresh) continue;
            }
            hardLogout();
            throw new Error('Session expired. Please sign in again.');
        }

        let data;
        try { data = await res.json(); } catch { data = {}; }
        if (res.ok) {
            // A save that went through is recorded in the person's name
            // (api/activityLog.js); a read sent as a POST is not.
            if (method !== 'GET' && method !== 'HEAD' && options.read !== true) recordActivity({ kind: 'erp', path, method, body: options.body });
            return data;
        }

        const errMsg = data.error || data.message || `HTTP ${res.status}`;
        // 4xx → bail; 5xx → retry until we run out of attempts.
        if (res.status < 500 || attempt >= MAX_RETRIES) {
            throw new Error(errMsg);
        }
        lastErr = new Error(errMsg);
        await sleep(BACKOFF_BASE * Math.pow(3, attempt));
    }
    // Loop only exits via return/throw above, this is unreachable but keeps
    // the type-checker happy if/when one of the early returns ever changes.
    throw lastErr;
}

const get  = (path)        => request(path);
// The activity log saves its rows here: a plain POST to the append-only
// audit log, marked as a read so it neither trips a view-only page's gate
// nor records itself.
setActivitySender((row) => request('/audit-log', { method: 'POST', body: JSON.stringify(row), read: true }));
// A read on an action-dispatch gateway: a POST that writes nothing, so no
// save guard and no write gate.
const postRead = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body), read: true });
// Writes run under the save guard: reloading/closing mid-save prompts the user.
const post = (path, body)  => guardSave(request(path, { method: 'POST',   body: JSON.stringify(body) }));
const put  = (path, body)  => guardSave(request(path, { method: 'PUT',    body: JSON.stringify(body) }));
const del  = (path)        => guardSave(request(path, { method: 'DELETE' }));

// Build a query string from a params object, omitting null/undefined values.
function qs(params) {
    const entries = Object.entries(params).filter(([, v]) => v != null);
    return entries.length ? '?' + new URLSearchParams(entries).toString() : '';
}

// ─── Vendors ──────────────────────────────────────────────────────────────────

export const vendorsApi = {
    list:   ()               => get('/vendors'),
    get:    (uuid)           => get(`/vendors/${encodeURIComponent(uuid)}`),
    create: (body)           => post('/vendors', body),
    update: (uuid, body)     => put(`/vendors/${encodeURIComponent(uuid)}`, body),
    remove: (uuid)           => del(`/vendors/${encodeURIComponent(uuid)}`),
};

// ─── Products ─────────────────────────────────────────────────────────────────

export const productsApi = {
    list:   ()               => get('/products'),
    get:    (id)             => get(`/products/${id}`),
    create: (body)           => post('/products', body),
    update: (id, body)       => put(`/products/${id}`, body),
    remove: (id)             => del(`/products/${id}`),
};

// ─── Raw Materials ────────────────────────────────────────────────────────────
// Editable overrides on top of the product-derived catalog. Hash key is `rm_id`
// (a UUID: v5-of-product-id for seeded rows, v4 for custom ones; rows saved
// under the old "RM-<productId>" scheme fold onto the UUID on read). Backed by the `raw-materials` DynamoDB
// table; until that endpoint is deployed the contexts fall back to the pure
// derived-from-products view.
export const rawMaterialsApi = {
    list:   ()               => get('/raw-materials'),
    get:    (id)             => get(`/raw-materials/${encodeURIComponent(id)}`),
    create: (body)           => post('/raw-materials', body),
    update: (id, body)       => put(`/raw-materials/${encodeURIComponent(id)}`, body),
    remove: (id)             => del(`/raw-materials/${encodeURIComponent(id)}`),
};

// ─── Yield / conversion batches ───────────────────────────────────────────────
// One row per production batch: a raw-material input (lb) and the finished
// products it turned into ({product_name, lb} outputs). Powers the
// /labels/yield Sankey; waste is DERIVED client-side (input − Σ outputs).
// Backed by the `yield-records` DynamoDB table (hash key `yield_id`).
export const yieldApi = {
    list:   ()               => get('/yield'),
    get:    (id)             => get(`/yield/${encodeURIComponent(id)}`),
    create: (body)           => post('/yield', body),
    update: (id, body)       => put(`/yield/${encodeURIComponent(id)}`, body),
    remove: (id)             => del(`/yield/${encodeURIComponent(id)}`),
};

// ─── Purchase Orders ──────────────────────────────────────────────────────────
// Hash key is `uuid` now. The user-facing label is `name`. Vendor reference
// is `vendor_uuid`. Org scope is `organization_uuid`. Cart items are `order_items`.

export const purchaseOrdersApi = {
    list:   ()               => get('/purchase-orders'),
    get:    (uuid)           => get(`/purchase-orders/${encodeURIComponent(uuid)}`),
    create: (body)           => post('/purchase-orders', body),
    update: (uuid, body)     => put(`/purchase-orders/${encodeURIComponent(uuid)}`, body),
    remove: (uuid)           => del(`/purchase-orders/${encodeURIComponent(uuid)}`),
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const invoicesApi = {
    list:   ()               => get('/invoices'),
    get:    (id)             => get(`/invoices/${encodeURIComponent(id)}`),
    create: (body)           => post('/invoices', body),
    update: (id, body)       => put(`/invoices/${encodeURIComponent(id)}`, body),
    remove: (id)             => del(`/invoices/${encodeURIComponent(id)}`),
};

// ─── Storage ──────────────────────────────────────────────────────────────────

export const storageApi = {
    list:   ()               => get('/storage'),
    get:    (id)             => get(`/storage/${id}`),
    create: (body)           => post('/storage', body),
    update: (id, body)       => put(`/storage/${id}`, body),
    remove: (id)             => del(`/storage/${id}`),
};

// ─── Label Presets ────────────────────────────────────────────────────────────

export const labelPresetsApi = {
    list:   ()               => get('/label-presets'),
    get:    (id)             => get(`/label-presets/${id}`),
    create: (body)           => post('/label-presets', body),
    update: (id, body)       => put(`/label-presets/${id}`, body),
    remove: (id)             => del(`/label-presets/${id}`),
};

// ─── Barcode Formats ──────────────────────────────────────────────────────────

export const barcodeFormatsApi = {
    list:   ()               => get('/barcode-formats'),
    get:    (id)             => get(`/barcode-formats/${id}`),
    create: (body)           => post('/barcode-formats', body),
    update: (id, body)       => put(`/barcode-formats/${id}`, body),
    remove: (id)             => del(`/barcode-formats/${id}`),
};

// ─── Staffs (composite key: business_id + staff_uuid) ────────────────────────
// business_id is passed as a query param for list/get/update/remove.
// Unlike the other ERP entities, staffs are per-brand-gateway: each brand's
// iPad apps validate PINs / temp codes against their OWN gateway's /staffs
// stack, so the dashboard must write to the same one. Build a client with
// staffsApiFor(config.staffsApiUrl); with no argument it targets the shared
// BASE gateway (All Kosher).

export function staffsApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        list:   (businessId)             => get(`${base}/staffs${qs({ business_id: businessId })}`),
        get:    (uuid, businessId)       => get(`${base}/staffs/${uuid}${qs({ business_id: businessId })}`),
        create: (body)                   => post(`${base}/staffs`, body),
        update: (uuid, businessId, body) => put(`${base}/staffs/${uuid}${qs({ business_id: businessId })}`, body),
        remove: (uuid, businessId)       => del(`${base}/staffs/${uuid}${qs({ business_id: businessId })}`),
    };
}

export const staffsApi = staffsApiFor('');

// ─── Scale Configs (composite key: business_id + "location#scale") ───────────
// Per-scale device IPs (label printer / specialty printer / raspberry Pi) that
// the Scale iPad app pulls into its local cache on launch. Per-brand-gateway
// like staffs: each brand's Scale app fetches from its OWN gateway's
// /scale-configs stack, so the dashboard must write to the same one. Build a
// client with scaleConfigsApiFor(config.scaleConfigsApiUrl); with no argument
// it targets the shared BASE gateway (All Kosher). Collection-only route: the
// row key is composite, so upserts carry the ids in the body and deletes in
// query params.

// ─── Kiosk Configs (composite key: business_id + "location#kiosk") ───────────
// Per-kiosk hardware: the PAX A35 terminal IP, receipt printer IP, TranCloud
// device id and merchant id. These lived hardcoded in the Kiosk iPad app's
// LocationKioskConfig.swift, which meant a register swap needed an app
// release; the app now reads them from here. Same shape and per-brand-gateway
// rules as scale configs.

// ─── Package Configs (composite key: business_id + "location#station") ──────
// Per-packing-station hardware. Two kinds of station share the table
// (utils/packageStations.js): ticket stations (the Star TSP100 that prints
// order tickets, and its gateway) and shipping stations (the label printer
// shipping labels print on). The Package iPad app reads this on launch so a
// printer swap needs no app release.
//
// `kind` narrows the list: 'ticket', 'shipping' or 'all'. With none the lambda
// answers ticket stations only, which is what Package builds from before
// shipping stations still ask for.

export function packageConfigsApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        list:   (businessId, kind)                 => get(`${base}/package-configs${qs({ business_id: businessId, kind })}`),
        save:   (body)                             => put(`${base}/package-configs`, body),
        remove: (businessId, locationId, stationId) => del(`${base}/package-configs${qs({ business_id: businessId, location_id: locationId, station_id: stationId })}`),
    };
}

export function kioskConfigsApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        list:   (businessId)                     => get(`${base}/kiosk-configs${qs({ business_id: businessId })}`),
        save:   (body)                           => put(`${base}/kiosk-configs`, body),
        remove: (businessId, locationId, kioskId) => del(`${base}/kiosk-configs${qs({ business_id: businessId, location_id: locationId, kiosk_id: kioskId })}`),
    };
}

export function scaleConfigsApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        list:   (businessId)                    => get(`${base}/scale-configs${qs({ business_id: businessId })}`),
        save:   (body)                          => put(`${base}/scale-configs`, body),
        remove: (businessId, locationId, scaleId) => del(`${base}/scale-configs${qs({ business_id: businessId, location_id: locationId, scale_id: scaleId })}`),
    };
}

// ─── Collaboration Users ──────────────────────────────────────────────────────
// business_id is passed as a query param for list.

export const collaborationApi = {
    list:   (businessId)     => get(`/collaboration${qs({ business_id: businessId })}`),
    get:    (userId)         => get(`/collaboration/${userId}`),
    create: (body)           => post('/collaboration', body),
    update: (userId, body)   => put(`/collaboration/${userId}`, body),
    remove: (userId)         => del(`/collaboration/${userId}`),
    // An owner or admin sets someone's sign-in password outright (the lambda
    // checks who is asking; the password is never stored).
    setPassword: (userId, password) => put(`/collaboration/${userId}`, { action: 'set_password', password }),
};

// ─── Locations (Stores) ──────────────────────────────────────────────────────

export const locationsApi = {
    list:   ()              => get('/locations'),
    get:    (uuid)          => get(`/locations/${encodeURIComponent(uuid)}`),
    create: (body)          => post('/locations', body),
    update: (uuid, body)    => put(`/locations/${encodeURIComponent(uuid)}`, body),
    remove: (uuid)          => del(`/locations/${encodeURIComponent(uuid)}`),
};

// ─── Wholesale Customers ─────────────────────────────────────────────────────

export const wholesaleCustomersApi = {
    list:   ()              => get('/wholesale-customers'),
    get:    (id)            => get(`/wholesale-customers/${encodeURIComponent(id)}`),
    create: (body)          => post('/wholesale-customers', body),
    update: (id, body)      => put(`/wholesale-customers/${encodeURIComponent(id)}`, body),
    remove: (id)            => del(`/wholesale-customers/${encodeURIComponent(id)}`),
    // Support documents (resale certificate, personal guarantee, credit
    // references…) live in the private bucket the storefront application
    // uploads to. docPresign → { upload_url, key, content_type } for a PUT
    // (needs no customer yet); docUrl → { url }, a short-lived GET for a file
    // listed on that customer's `documents`.
    docPresign: (filename, contentType) => post('/wholesale-customers', { action: 'doc_presign', filename, content_type: contentType }),
    docUrl:     (id, key)   => post(`/wholesale-customers/${encodeURIComponent(id)}`, { action: 'doc_url', key }),
};

// ─── Wholesale Orders ────────────────────────────────────────────────────────

export const wholesaleOrdersApi = {
    list:   ()              => get('/wholesale-orders'),
    get:    (id)            => get(`/wholesale-orders/${encodeURIComponent(id)}`),
    create: (body)          => post('/wholesale-orders', body),
    update: (id, body)      => put(`/wholesale-orders/${encodeURIComponent(id)}`, body),
    remove: (id)            => del(`/wholesale-orders/${encodeURIComponent(id)}`),
    // POST to the item path triggers the lambda's send-invoice action: it looks up
    // the customer by customer_id, emails them the order details + amount due via
    // SMTP, and stamps invoice_sent_at on the order row.
    sendInvoice: (id)       => post(`/wholesale-orders/${encodeURIComponent(id)}`, {}),
};

// ─── Wholesale Applications ──────────────────────────────────────────────────
// Incoming wholesale sign-ups from the romaniankosher.com storefront. Reviewed
// here: approve mints a wholesale-customers row + emails the applicant; decline
// emails them. Both side effects run in the lambda (POST to the item path).

export const wholesaleApplicantsApi = {
    list:     ()             => get('/wholesale-applications'),
    get:      (id)           => get(`/wholesale-applications/${encodeURIComponent(id)}`),
    update:   (id, body)     => put(`/wholesale-applications/${encodeURIComponent(id)}`, body),
    remove:   (id)           => del(`/wholesale-applications/${encodeURIComponent(id)}`),
    markRead: (id)           => put(`/wholesale-applications/${encodeURIComponent(id)}`, { read: true }),
    // details = { credit_limit, terms, risk, account_status, credit_notes }, the
    // credit decision the manager fills in at approval; stamped onto the new customer.
    approve:  (id, details)  => post(`/wholesale-applications/${encodeURIComponent(id)}`, { action: 'approve', ...(details || {}) }),
    decline:  (id, reason)   => post(`/wholesale-applications/${encodeURIComponent(id)}`, { action: 'decline', reason }),
    // Short-lived presigned GET for a private support document (bucket is not public).
    docUrl:   (id, key)      => post(`/wholesale-applications/${encodeURIComponent(id)}`, { action: 'doc_url', key }),
};

// ─── Wholesale Product Pricing ───────────────────────────────────────────────
// One row per product_id: the manager's base cost + any per-level price overrides.
// Default level prices are derived client-side (base_cost x the level markup %);
// only overrides are persisted. update() upserts (PUT creates the row if absent).
export const wholesaleProductPricingApi = crudApi('/wholesale-product-pricing');

// ─── Organizations (per-owner orgs Sam manages) ──────────────────────────────

export const organizationsApi = {
    list:   (ownerEmail)   => get(`/organizations${qs({ owner_email: ownerEmail })}`),
    get:    (id)           => get(`/organizations/${encodeURIComponent(id)}`),
    create: (body)         => post('/organizations', body),
    update: (id, body)     => put(`/organizations/${encodeURIComponent(id)}`, body),
    remove: (id, code)     => del(`/organizations/${encodeURIComponent(id)}${qs({ code })}`),
    // POST /organizations/{id} with `{action:'send_delete_code'}` triggers an email
    // to owner_email containing a 6-digit confirmation code.
    sendDeleteCode: (id)   => post(`/organizations/${encodeURIComponent(id)}`, { action: 'send_delete_code' }),
};

// ─── Settings (keyed by business_id) ─────────────────────────────────────────

export const settingsApi = {
    get:  (businessId)        => get(`/settings/${encodeURIComponent(businessId)}`),
    save: (businessId, body)  => put(`/settings/${encodeURIComponent(businessId)}`, body),
};

// ─── Workspace mailbox (every email the dashboard sends) ─────────────────────
// The `gmail` Lambda keeps each workspace's own sending address and its Google
// app password (owner, 2026-10-28: "every email sent out ... through dashboard
// should not use personally connected email mailboxes"; "e.g. info@allkosher.com
// is for all emails going out of all kosher workspace"). `status` answers
// { configured, address, password_last4, updated_at, updated_by }; `save`
// signs in at Google once before keeping the password ({ clear: true } removes
// the mailbox); `send` one message, HTML with PDFs attached as base64.
export const mailboxApi = {
    status: (workspace) => get(`/gmail/status${qs({ workspace })}`),
    save:   (body)      => post('/gmail/mailbox', body),
    send:   (body)      => post('/gmail/send', body),
    // Settings' Reveal: the app password in full ({ app_password }), for an
    // Admin of the workspace only. Writes nothing.
    reveal: (workspace) => postRead('/gmail/mailbox', { workspace, reveal: true }),
};

// ─── Commerce Control layer ───────────────────────────────────────────────────
// The financial-control / reconciliation entities from the Romanian Commerce
// Control spec. Each is a scan+org-filter table behind the same API Gateway, so
// the clients are uniform CRUD. The item path param is a generic `{id}` that the
// lambda maps onto that table's hash key.

function crudApi(path) {
    return {
        list:   ()           => get(path),
        get:    (id)         => get(`${path}/${encodeURIComponent(id)}`),
        create: (body)       => post(path, body),
        update: (id, body)   => put(`${path}/${encodeURIComponent(id)}`, body),
        remove: (id)         => del(`${path}/${encodeURIComponent(id)}`),
    };
}

export const cashSessionsApi     = crudApi('/cash-sessions');     // daily-close / Z-report drawer sessions
export const depositsApi         = crudApi('/deposits');          // cash/check/processor deposits to the bank
export const payoutsApi          = crudApi('/payouts');           // Stripe/PayPal/EBT settlement batches
// The same table on a workspace's OWN gateway (Romanian's processor sync writes
// there, where the shared gateway never looks): pass that gateway's /payouts URL.
export const payoutsLedgerApi    = (url) => crudApi(url);
export const bankTransactionsApi = crudApi('/bank-transactions'); // imported bank-feed rows
export const refundsApi          = crudApi('/refunds');           // refund/void control records
export const giftCertificatesApi = crudApi('/gift-certificates'); // gift-cert liability ledger
export const qbPostingsApi       = crudApi('/qb-postings');       // daily QuickBooks journal entries
// audit-log is append-only on the backend (PUT/DELETE 405), only list/get/create are used.
export const auditLogApi         = crudApi('/audit-log');
// dock sign-off reports from the iPad receiver (one per PO receiving session)
export const receivingReportsApi = crudApi('/receiving-reports');

// ─── Gift cards (each brand's gift-cards lambda) ─────────────────────────────
// Every card the brand's gift-cards lambda holds: the register's physical and
// emailed cards (and, for All Kosher, the storefront's too). Read from the
// token-gated /gift-cards-admin route, never /gift-cards, which is open to the
// iPads and the storefront and so refuses to enumerate redemption codes. All
// Kosher's route sits on the shared BASE gateway; Romanian's on its own
// gateway (pass its base URL) behind an authorizer on this dashboard's user
// pool, so the ID token goes along either way. Pages until next_cursor is
// null: stopping at a short page would under-report the liability.
const GIFT_CARDS_ADMIN_MAX_PAGES = 50;

export async function fetchAllGiftCardsAdmin(gatewayUrl) {
    const base = gatewayUrl ? gatewayUrl.replace(/\/$/, '') : '';
    const cards = [];
    let cursor = null;

    for (let page = 0; page < GIFT_CARDS_ADMIN_MAX_PAGES; page++) {
        const data = await request(`${base}/gift-cards-admin${qs({ limit: 1000, cursor })}`, { authorize: true });
        if (Array.isArray(data.gift_cards)) cards.push(...data.gift_cards);

        cursor = data.next_cursor || null;
        if (!cursor) break;
    }

    return cards;
}

// ─── Deleted records (write provenance) ─────────────────────────────────────
// Every row a lambda deleted, with the caller's IP, from the delete log each
// AWS account keeps (utils/writeProvenance.js). All Kosher's is on the shared
// BASE gateway; Romanian's on its own gateway (pass its base URL) behind an
// authorizer on this dashboard's user pool, so the ID token goes along either
// way. from / to are UTC days ('YYYY-MM-DD'), at most 93 apart.
export async function fetchDeletedRecords(gatewayUrl, { from, to } = {}) {
    const base = gatewayUrl ? gatewayUrl.replace(/\/$/, '') : '';
    const data = await request(`${base}/deleted-records${qs({ from, to })}`, { authorize: true });
    return Array.isArray(data.items) ? data.items : [];
}

// ─── Delivery live tracking (driver GPS) ────────────────────────────────────
// The /delivery endpoint is secret-gated. The dashboard's Drivers read goes
// through the SAME-ORIGIN proxy (dev setupProxy / prod CloudFront function),
// which injects the shared secret server-side, so it never ships in the
// browser bundle (mirrors the kiosk/woocommerce proxy pattern). Hence a raw
// fetch to the relative /api/allkosher/delivery path, not the API-Gateway BASE.
const DELIVERY_PROXY_URL = '/api/allkosher/delivery';

// Per-brand, same rule as staffs / scale-configs / package: each workspace's
// drivers report to its OWN gateway (Romanian's Delivery app posts to
// u0pypgh32a), so reading All Kosher's stack for Romanian showed zero drivers
// and a Florida map while the Chicago fixes sat in the other account.
export function deliveryApiFor(proxyUrl) {
    const url = proxyUrl || DELIVERY_PROXY_URL;
    return {
        // Each driver's ordered breadcrumb trail + their latest fix in a window.
        //   sinceMs / untilMs  window bounds (backend defaults: last 24h → now).
        //                      The backend clamps sinceMs to the retention window
        //                      and echoes back the since/until/retention_days it
        //                      actually used, so the UI can say when a requested
        //                      range reached further back than the data goes.
        //   staffUuids         the roster to read. Passing it turns the backend
        //                      from a full table scan into one keyed query per
        //                      driver, which is what makes a wide date range
        //                      affordable, always pass it when it is known.
        listDriverTracks: async (sinceMs, untilMs, staffUuids) => {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'list_driver_tracks',
                    ...(sinceMs ? { since: sinceMs } : null),
                    ...(untilMs ? { until: untilMs } : null),
                    ...(staffUuids && staffUuids.length ? { staff_uuids: staffUuids } : null),
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        },
    };
}

// Security tab feed, read-only rows written by the telemetry lambda's mirror
// (failed PINs, refunds/voids/discounts, scan deletions, stock adjustments,
// crashes/errors). Filters: app, category, level, event, staff_uuid, store_id,
// since/until (ms epoch), limit.
// Each brand's apps report telemetry to their OWN gateway's security-events
// table (the Romanian iPads never write to the All Kosher stack), so the feed
// is per-workspace like staffs / scale-configs: build one with
// securityEventsApiFor(config.securityEventsApiUrl); no argument targets the
// shared BASE gateway (All Kosher).
export function securityEventsApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        list: (params = {}) => get(`${base}/security-events${qs(params)}`),
    };
}

export const securityEventsApi = securityEventsApiFor('');

// ─── Package realtime service (order locks · notes · staff notifications) ────
// One POST /package endpoint dispatching on `action`, the same lambda that
// serves the Package iPad's REST + websocket. The dashboard uses it to watch
// live order locks, release them, manage the shared notes, and send staff
// notifications to app devices over the realtime channel.
// Per-brand, same rule as staffs/scale-configs: each workspace's Package stack
// lives on its OWN gateway (Romanian runs u0pypgh32a, All Kosher the shared
// BASE), so the dashboard must talk to the one that owns that workspace's data
// rather than leaking calls across tenants.
export function packageApiFor(baseUrl) {
    const base = (baseUrl || '').replace(/\/$/, '');
    return {
        // opts: { app? } narrows the board to one store app's locks ('package'
        // for WooCommerce orders, 'inventory' for purchase orders).
        listOrderLocks:    (opts = {}) => post(`${base}/package`, { action: 'list_order_locks', ...opts }),
        adminUnlockOrder:  (orderId, unlockedBy) => post(`${base}/package`, { action: 'admin_unlock_order', order_id: orderId, unlocked_by: unlockedBy }),
        listNotes:         (opts = {}) => post(`${base}/package`, { action: 'list_notes', ...opts }),
        upsertNote:        (note) => post(`${base}/package`, { action: 'upsert_note', ...note }),
        deleteNote:        (noteId, staffUuid) => post(`${base}/package`, { action: 'delete_note', note_id: noteId, staff_uuid: staffUuid }),
        listNotifications: (opts = {}) => post(`${base}/package`, { action: 'list_notifications', ...opts }),
        sendStaffNotification: (payload) => post(`${base}/package`, { action: 'send_staff_notification', ...payload }),
        // Box contents, the recipe for a box SKU (what is inside it), owned by
        // Applications → Package → Settings and cached on every Package iPad.
        listBoxContents:   ()     => post(`${base}/package`, { action: 'list_box_contents' }),
        upsertBoxContents: (box)  => post(`${base}/package`, { action: 'upsert_box_contents', ...box }),
        deleteBoxContents: (boxId) => post(`${base}/package`, { action: 'delete_box_contents', box_id: boxId }),
        // Station settings, the assistant's OpenAI key + model tiers, pulled by
        // every Package iPad. The secret unlocks the key on read and gates the
        // write; without it the key comes back masked. The ShipStation key
        // (shipstation_api_key) is write-only: only ever read back masked.
        getPackageSettings:    (secret)          => post(`${base}/package`, { action: 'get_package_settings', secret }),
        upsertPackageSettings: (secret, payload) => post(`${base}/package`, { action: 'upsert_package_settings', secret, ...payload }),
        // Settings' Reveal: one stored key in full ({ value }), for an Admin
        // of the workspace only. The lambda checks the dashboard sign-in, so
        // the ID token goes to the per-brand gateway too (`authorize`).
        revealSettingsSecret:  (secret, field)   => request(`${base}/package`, {
            method: 'POST', read: true, authorize: true,
            body: JSON.stringify({ action: 'reveal_settings_secret', secret, field }),
        }),
        // Settings › ShipStation. Status is what ShipStation makes of the key
        // on file ({ configured, key_masked, environment 'sandbox' |
        // 'production', reachable, error, carriers }). Connect checks a key
        // with ShipStation before saving it, so a refused key never replaces
        // a working one; { clear: true } disconnects. The key is never read back.
        shipstationStatus:     (secret)          => postRead(`${base}/package`, { action: 'shipstation_status', secret }),
        shipstationConnect:    (secret, payload) => post(`${base}/package`, { action: 'shipstation_connect', secret, ...payload }),
        // Applications › Package › ShipStation Presets: the box sizes the
        // Package app offers ({ presets: [{ package_id, package_code, name,
        // length_in, width_in, height_in }] }, the iPad's own list and order).
        // `fresh` reads ShipStation now instead of the lambda's 2-minute cache.
        shipstationPresets:    (secret, opts = {}) => postRead(`${base}/package`, { action: 'shipstation_presets', secret, ...opts }),
        // The labels ledger: every shipping label the Package app bought in a
        // window (by purchase date), with what it cost, for Sales › Shipping.
        listShippingLabels:    (opts = {})       => postRead(`${base}/package`, { action: 'shipping_labels_list', ...opts }),
    };
}

export const packageApi = {
    listOrderLocks:    (opts = {}) => post('/package', { action: 'list_order_locks', ...opts }),
    adminUnlockOrder:  (orderId, unlockedBy) => post('/package', { action: 'admin_unlock_order', order_id: orderId, unlocked_by: unlockedBy }),
    // opts: { location_id? }, scope notes to a store ('' / omitted = all).
    listNotes:         (opts = {}) => post('/package', { action: 'list_notes', ...opts }),
    upsertNote:        (note) => post('/package', { action: 'upsert_note', ...note }),
    deleteNote:        (noteId, staffUuid) => post('/package', { action: 'delete_note', note_id: noteId, staff_uuid: staffUuid }),
    // opts: { kind?, app? } filter server-side BEFORE the newest-100 cap, so a
    // burst of order notifications can't evict the staff-message list.
    listNotifications: (opts = {}) => post('/package', { action: 'list_notifications', ...opts }),
    // { title, message, level, app, staff_uuids, deliver_at?, sender_name, client_key }
    // client_key makes the send idempotent, a retried POST (5xx/timeout) reuses
    // the row instead of creating a duplicate staff message.
    sendStaffNotification: (payload) => post('/package', { action: 'send_staff_notification', ...payload }),
    // Box contents, see packageApiFor above.
    listBoxContents:   ()      => post('/package', { action: 'list_box_contents' }),
    upsertBoxContents: (box)   => post('/package', { action: 'upsert_box_contents', ...box }),
    deleteBoxContents: (boxId) => post('/package', { action: 'delete_box_contents', box_id: boxId }),
    getPackageSettings:    (secret)          => post('/package', { action: 'get_package_settings', secret }),
    upsertPackageSettings: (secret, payload) => post('/package', { action: 'upsert_package_settings', secret, ...payload }),
};

// ─── Dashboard overlay (tester-safe write layer) ─────────────────────────────
// The ONE table the dashboard writes to. Command Center resolutions, manual
// adjustments and any other "save" from the UI land here, deliberately kept OFF
// the raw dataset (orders/transactions). Fully editable/deletable so a tester
// can undo anything by clearing rows, with zero risk to the real numbers.
export const dashboardOverridesApi = crudApi('/dashboard-overrides');

// ─── Email Audience (Sales › Marketing) ──────────────────────────────────────
// A workspace's marketing list: who is subscribed, who unsubscribed, and every
// change to either. `list` pages through the whole set. The website sync runs
// server-side (the Lambda pulls the store's customer accounts itself), so no
// customer list ever passes through the browser.
export const emailAudienceApi = {
    // list: 'subscribed' | 'unsubscribed' | 'activity'
    list: async (businessId, list) => {
        const items = [];
        let next = null;
        do {
            const page = await get(`/email-audience${qs({ business_id: businessId, list, next })}`);
            items.push(...(page.items || []));
            next = page.next || null;
        } while (next);
        return items;
    },
    // { email | emails, first_name?, last_name?, resubscribe? }
    subscribe:   (businessId, body) => post('/email-audience', { action: 'subscribe', business_id: businessId, ...body }),
    // { email | emails, first_name?, last_name?, reason? }
    unsubscribe: (businessId, body) => post('/email-audience', { action: 'unsubscribe', business_id: businessId, ...body }),
    syncWebsite: (businessId) => post('/email-audience', { action: 'sync', business_id: businessId }),
};

// ─── Support tickets ─────────────────────────────────────────────────────────
// Every workspace asks Wavelength for help from its Support page, and the
// Wavelength team works the queue from admin.wavelengtherp.com. The lambda
// knows who is asking from the signed-in token: a workspace reads its own
// tickets, the Wavelength team reads every workspace's (`listAll`).
export const supportTicketsApi = {
    // { email, name, is_admin }: is this account on the Wavelength team?
    session:  ()                => get('/support-tickets?view=session'),
    list:     (businessId)      => get(`/support-tickets${qs({ business_id: businessId })}`),
    listAll:  ()                => get('/support-tickets?scope=all'),
    // { item, messages }
    get:      (id)              => get(`/support-tickets/${encodeURIComponent(id)}`),
    // { business_id, business_name, subject, description, category, priority, page_url?, attachments? }
    create:   (body)            => post('/support-tickets', body),
    // { body, attachments? }
    reply:    (id, body)        => put(`/support-tickets/${encodeURIComponent(id)}`, { action: 'reply', ...body }),
    // { status?, priority?, category?, assignee? }
    update:   (id, fields)      => put(`/support-tickets/${encodeURIComponent(id)}`, { action: 'update', ...fields }),
};

// ─── Wavelength team records (admin.wavelengtherp.com) ───────────────────────
// Business onboarding (deployments) and what each business pays Wavelength
// (subscriptions, invoices, payments). Only the Wavelength team gets answers.
// kind: 'deployment' | 'subscription' | 'invoice' | 'payment'. Money in cents.
export const adminRecordsApi = {
    list:   (kind, businessId) => get(`/admin-records${qs({ kind, business_id: businessId })}`),
    get:    (id)               => get(`/admin-records/${encodeURIComponent(id)}`),
    create: (body)             => post('/admin-records', body),
    update: (id, body)         => put(`/admin-records/${encodeURIComponent(id)}`, body),
    remove: (id)               => del(`/admin-records/${encodeURIComponent(id)}`),
};

// ─── Bank statements (Command Center > Bank Statements > Upload statement) ───
// A PDF statement is read by the bank-statements lambda: it splits the PDF
// into pages and reads each with Textract, answering every line with its
// place on the page ({ page_count, pages: [{ page, lines: [{ text, left, top,
// width, height, conf }] }] }); utils/bankStatementPdf.js rebuilds the
// statement from that. Nothing is stored server side. Not a save, so no save
// guard; a 4 MB cap keeps the base64 under the gateway's request limit.
export const bankStatementsApi = {
    readPdf: (fileBase64, filename) => request('/bank-statements', {
        method: 'POST',
        body: JSON.stringify({ action: 'ocr', file_base64: fileBase64, filename }),
    }),
};

// ─── Remote card refunds ─────────────────────────────────────────────────────
// A register sale's card refunded from the dashboard: queued here for the
// store's master server, which runs it on the card reader and reports back
// (remote-refunds lambda; utils/remoteRefunds). Cognito routes on this
// gateway for both workspaces, scoped by business_id.
export const remoteRefundsApi = {
    list:    (businessId, orderId) => get(`/remote-refunds${qs({ business_id: businessId, order_id: orderId })}`),
    queue:   (body)                => post('/remote-refunds', body),
    act:     (refundId, body)      => post(`/remote-refunds/${encodeURIComponent(refundId)}`, body),
    servers: (businessId)          => get(`/master-servers${qs({ business_id: businessId })}`),
};
